import { create } from "zustand";
import { persist } from "zustand/middleware";

interface AiAssistantStore {
	isOpen: boolean;
	/**
	 * Conversation shown in the sidebar. Undefined = the empty state; the
	 * first message then starts a new conversation.
	 */
	activeConversationId?: string;
	/**
	 * True when the user chose the current selection themselves (history,
	 * "Fortsätt senaste", "Ny konversation") or sent a message into it. An
	 * explicit selection is left alone for the rest of the browser session;
	 * an automatic one is re-resolved from the newest conversation whenever
	 * the sidebar opens or the tab regains focus.
	 */
	isExplicitSelection: boolean;
	open: () => void;
	close: () => void;
	toggle: () => void;
	/** The user picked this conversation (or none, for a fresh start). */
	selectConversation: (conversationId?: string) => void;
	/** The sidebar resolved this conversation on its own. */
	resumeConversation: (conversationId?: string) => void;
}

/**
 * UI state for the AI assistant sidebar. Only the open/closed state is
 * persisted: which conversation to show is decided from the server list
 * each time the panel opens, so a reload or a new tab never resurrects a
 * thread the user finished hours ago. Conversation content lives in React
 * Query (`useAiConversation`).
 */
export const useAiAssistantStore = create<AiAssistantStore>()(
	persist(
		(set) => ({
			isOpen: false,
			activeConversationId: undefined,
			isExplicitSelection: false,
			open: () => set({ isOpen: true }),
			close: () => set({ isOpen: false }),
			toggle: () => set((state) => ({ isOpen: !state.isOpen })),
			selectConversation: (conversationId) =>
				set({ activeConversationId: conversationId, isExplicitSelection: true }),
			resumeConversation: (conversationId) =>
				set({ activeConversationId: conversationId, isExplicitSelection: false }),
		}),
		{
			name: "ai-assistant",
			version: 1,
			partialize: (state) => ({ isOpen: state.isOpen }),
		}
	)
);
